import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SmartTemplateService } from '../smart-template.service';
import Swal from 'sweetalert2';

export interface TemplateParameter {
    id?: number;
    name: string;
    parameter_key: string;
    description?: string;
    type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'textarea';
    category: 'system' | 'user' | 'document' | 'organization';
    default_value?: string;
    options?: string;
    validation_rules?: string;
    is_required: boolean;
    is_active: boolean;
    is_global: boolean;
    usage_count?: number;
    used_in_templates?: any[];
    created_at?: string;
    updated_at?: string;
    _isModified?: boolean;
    _expanded?: boolean;
}

@Component({
    selector: 'app-template-parameter',
    templateUrl: './template-parameter.component.html',
    styleUrls: ['./template-parameter.component.scss']
})
export class TemplateParameterComponent implements OnInit {
    isSaving = false;
    showEditor = false;
    editingParameter: TemplateParameter | null = null;

    // Data collections
    parameters: TemplateParameter[] = [];
    filteredParameters: TemplateParameter[] = [];
    selectedParameters: number[] = [];

    // Filter values
    searchTerm = '';
    selectedType = '';
    selectedCategory = '';
    showOnlyRequired = false;
    showOnlyActive = false;

    constructor(
        @Optional() public dialogRef: MatDialogRef<TemplateParameterComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
        private smartTemplateService: SmartTemplateService
    ) { }

    onClose(): void {
        this.dialogRef.close({});
    }

    ngOnInit(): void {
        this.loadParameters();
    }

    /**
     * Load all parameters
     */
    loadParameters(): void {
        this.smartTemplateService.getTemplateParameters().subscribe(
            (response: any) => {
                if (response.success && response.data) {
                    this.parameters = response.data.map((param: TemplateParameter) => ({
                        ...param,
                        _expanded: false,
                        _isModified: false
                    }));
                    this.applyFilters();
                }
            },
            (error) => {
                console.error('Error loading parameters:', error);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-au putut încărca parametrii.',
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Apply filters to parameters
     */
    applyFilters(): void {
        let filtered = [...this.parameters];

        // Search filter
        if (this.searchTerm) {
            const searchLower = this.searchTerm.toLowerCase();
            filtered = filtered.filter(param =>
                param.name.toLowerCase().includes(searchLower) ||
                param.parameter_key.toLowerCase().includes(searchLower) ||
                (param.description && param.description.toLowerCase().includes(searchLower))
            );
        }

        // Type filter
        if (this.selectedType) {
            filtered = filtered.filter(param => param.type === this.selectedType);
        }

        // Category filter
        if (this.selectedCategory) {
            filtered = filtered.filter(param => param.category === this.selectedCategory);
        }

        // Required filter
        if (this.showOnlyRequired) {
            filtered = filtered.filter(param => param.is_required);
        }

        // Active filter
        if (this.showOnlyActive) {
            filtered = filtered.filter(param => param.is_active);
        }

        this.filteredParameters = filtered;
    }

    /**
     * Clear all filters
     */
    clearFilters(): void {
        this.searchTerm = '';
        this.selectedType = '';
        this.selectedCategory = '';
        this.showOnlyRequired = false;
        this.showOnlyActive = false;
        this.applyFilters();
    }

    /**
     * Refresh parameters
     */
    refreshParameters(): void {
        this.loadParameters();
    }

    /**
     * Add new parameter
     */
    addParameter(): void {
        this.editingParameter = {
            name: '',
            parameter_key: '',
            description: '',
            type: 'text',
            category: 'user',
            default_value: '',
            options: '',
            validation_rules: '',
            is_required: false,
            is_active: true,
            is_global: false
        };
        this.showEditor = true;
    }

    /**
     * Edit parameter
     */
    editParameter(parameter: TemplateParameter): void {
        this.editingParameter = { ...parameter };
        this.showEditor = true;
    }

    /**
     * Duplicate parameter
     */
    duplicateParameter(parameter: TemplateParameter): void {
        this.editingParameter = {
            ...parameter,
            id: undefined,
            name: parameter.name + ' - Copie',
            parameter_key: parameter.parameter_key + '_copy',
            created_at: undefined,
            updated_at: undefined
        };
        this.showEditor = true;
    }

    /**
     * Delete parameter
     */
    deleteParameter(parameter: TemplateParameter): void {
        if (parameter.usage_count && parameter.usage_count > 0) {
            Swal.fire({
                title: 'Atenție',
                text: `Acest parametru este utilizat în ${parameter.usage_count} template-uri. Sigur doriți să îl ștergeți?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Da, șterge',
                cancelButtonText: 'Anulează',
                confirmButtonColor: '#d33'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.performDeleteParameter(parameter);
                }
            });
        } else {
            Swal.fire({
                title: 'Confirmare ștergere',
                text: 'Sigur doriți să ștergeți acest parametru?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Șterge',
                cancelButtonText: 'Anulează'
            }).then((result) => {
                if (result.isConfirmed) {
                    this.performDeleteParameter(parameter);
                }
            });
        }
    }

    /**
     * Perform parameter deletion
     */
    private performDeleteParameter(parameter: TemplateParameter): void {
        if (!parameter.id) return;

        this.smartTemplateService.deleteTemplateParameter(parameter.id).subscribe(
            (response: any) => {
                if (response.success) {
                    this.parameters = this.parameters.filter(p => p.id !== parameter.id);
                    this.applyFilters();
                    Swal.fire({
                        title: 'Succes',
                        text: 'Parametru șters cu succes!',
                        icon: 'success'
                    });
                }
            },
            (error) => {
                console.error('Error deleting parameter:', error);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-a putut șterge parametrul.',
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Save parameter
     */
    saveParameter(): void {
        if (!this.editingParameter || !this.isParameterValid()) return;

        this.isSaving = true;

        const saveOperation = this.editingParameter.id
            ? this.smartTemplateService.updateTemplateParameter(this.editingParameter.id, this.editingParameter)
            : this.smartTemplateService.createTemplateParameter(this.editingParameter);

        saveOperation.subscribe(
            (response: any) => {
                this.isSaving = false;
                if (response.success) {
                    if (this.editingParameter!.id) {
                        // Update existing parameter
                        const index = this.parameters.findIndex(p => p.id === this.editingParameter!.id);
                        if (index > -1) {
                            this.parameters[index] = { ...response.data, _expanded: false, _isModified: false };
                        }
                    } else {
                        // Add new parameter
                        this.parameters.push({ ...response.data, _expanded: false, _isModified: false });
                    }

                    this.applyFilters();
                    this.closeEditor();

                    Swal.fire({
                        title: 'Succes',
                        text: `Parametru ${this.editingParameter!.id ? 'actualizat' : 'creat'} cu succes!`,
                        icon: 'success'
                    });
                }
            },
            (error) => {
                this.isSaving = false;
                console.error('Error saving parameter:', error);

                let errorMessage = 'A apărut o eroare neașteptată.';
                if (error.error && error.error.message) {
                    errorMessage = error.error.message;
                }

                Swal.fire({
                    title: 'Eroare',
                    text: errorMessage,
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Close parameter editor
     */
    closeEditor(): void {
        this.showEditor = false;
        this.editingParameter = null;
    }

    /**
     * Validate parameter
     */
    isParameterValid(): boolean {
        if (!this.editingParameter) return false;

        return !!(this.editingParameter.name &&
            this.editingParameter.name.trim().length > 0 &&
            this.editingParameter.parameter_key &&
            this.editingParameter.parameter_key.trim().length > 0 &&
            this.editingParameter.type &&
            this.editingParameter.category);
    }

    /**
     * Parameter selection methods
     */
    isParameterSelected(parameterId?: number): boolean {
        if (!parameterId) return false;
        return this.selectedParameters.includes(parameterId);
    }

    toggleParameterSelection(event: any, parameterId?: number): void {
        if (!parameterId) return;

        if (event.checked) {
            if (!this.selectedParameters.includes(parameterId)) {
                this.selectedParameters.push(parameterId);
            }
        } else {
            this.selectedParameters = this.selectedParameters.filter(id => id !== parameterId);
        }
    }

    allParametersSelected(): boolean {
        const visibleIds = this.filteredParameters.map(p => p.id).filter(id => id !== undefined) as number[];
        return visibleIds.length > 0 && visibleIds.every(id => this.selectedParameters.includes(id));
    }

    someParametersSelected(): boolean {
        const visibleIds = this.filteredParameters.map(p => p.id).filter(id => id !== undefined) as number[];
        return visibleIds.some(id => this.selectedParameters.includes(id)) && !this.allParametersSelected();
    }

    toggleAllParameters(event: any): void {
        const visibleIds = this.filteredParameters.map(p => p.id).filter(id => id !== undefined) as number[];

        if (event.checked) {
            visibleIds.forEach(id => {
                if (!this.selectedParameters.includes(id)) {
                    this.selectedParameters.push(id);
                }
            });
        } else {
            this.selectedParameters = this.selectedParameters.filter(id => !visibleIds.includes(id));
        }
    }

    /**
     * Bulk operations
     */
    bulkActivateParameters(): void {
        this.performBulkOperation('activate', 'activat');
    }

    bulkDeactivateParameters(): void {
        this.performBulkOperation('deactivate', 'dezactivat');
    }

    bulkDeleteParameters(): void {
        Swal.fire({
            title: 'Confirmare ștergere în masă',
            text: `Sigur doriți să ștergeți ${this.selectedParameters.length} parametri selectați?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Da, șterge',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#d33'
        }).then((result) => {
            if (result.isConfirmed) {
                this.performBulkOperation('delete', 'șters');
            }
        });
    }

    private performBulkOperation(operation: string, actionLabel: string): void {
        if (this.selectedParameters.length === 0) return;

        this.isSaving = true;
        const data = {
            parameter_ids: this.selectedParameters,
            operation: operation
        };

        this.smartTemplateService.bulkUpdateParameters(data).subscribe(
            (response: any) => {
                this.isSaving = false;
                if (response.success) {
                    if (operation === 'delete') {
                        this.parameters = this.parameters.filter(p => !this.selectedParameters.includes(p.id!));
                    } else {
                        // Update parameters based on operation
                        this.parameters = this.parameters.map(p => {
                            if (this.selectedParameters.includes(p.id!)) {
                                return { ...p, is_active: operation === 'activate' };
                            }
                            return p;
                        });
                    }

                    this.selectedParameters = [];
                    this.applyFilters();

                    Swal.fire({
                        title: 'Succes',
                        text: `Parametrii au fost ${actionLabel} cu succes!`,
                        icon: 'success'
                    });
                }
            },
            (error) => {
                this.isSaving = false;
                console.error(`Error performing bulk ${operation}:`, error);
                Swal.fire({
                    title: 'Eroare',
                    text: `Nu s-au putut ${actionLabel} parametrii.`,
                    icon: 'error'
                });
            }
        );
    }

    /**
     * Helper methods
     */
    getTypeLabel(type: string): string {
        const labels: { [key: string]: string } = {
            'text': 'Text',
            'number': 'Număr',
            'date': 'Dată',
            'boolean': 'Da/Nu',
            'select': 'Listă',
            'textarea': 'Text lung'
        };
        return labels[type] || type;
    }

    getCategoryLabel(category: string): string {
        const labels: { [key: string]: string } = {
            'system': 'Sistem',
            'user': 'Utilizator',
            'document': 'Document',
            'organization': 'Organizație'
        };
        return labels[category] || category;
    }

    getOptionsArray(options: string): string[] {
        if (!options) return [];
        return options.split(',').map(opt => opt.trim()).filter(opt => opt.length > 0);
    }

    trackParameter(index: number, item: TemplateParameter): number | undefined {
        return item.id;
    }

    /**
     * Close dialog
     */
    close(): void {
        if (this.dialogRef) {
            this.dialogRef.close({ success: false });
        }
    }
}